import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ScanSearch, Database, Columns3, Route, ShieldCheck, ShieldAlert,
  HelpCircle, ArrowRight, Sparkles, BadgeCheck, BookmarkPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { ASK_PREVIEW } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const EXAMPLES = [
  "Which facility has the most hospital readmissions?",
  "What is the average length of stay by facility?",
  "Show monthly revenue trend across the portfolio",
  "How many residents are currently admitted?",
];

const INTENT_LABEL = {
  metric_lookup: "Metric lookup", trend: "Trend", comparison: "Comparison",
  diagnostic: "Diagnostic", exploration: "Exploration", schema_question: "Schema",
};

function confColor(c) {
  if (c >= 0.85) return "bg-accent";
  if (c >= 0.75) return "bg-emerald-500";
  if (c >= 0.6) return "bg-amber-500";
  return "bg-muted-foreground";
}

function ConfidenceBar({ value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div data-testid={ASK_PREVIEW.confidence} className="flex items-center gap-2">
      <div className="h-1.5 w-28 overflow-hidden rounded-full bg-border/60">
        <div className={`h-full ${confColor(value)}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[11px] tabular-nums text-muted-foreground">{pct}% confidence</span>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border/60 bg-card/40 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-4 w-4 text-accent" />
        <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function AskPreview() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMetric, setSavedMetric] = useState(null);
  const [error, setError] = useState(null);

  const run = async (q) => {
    const text = (q ?? question).trim();
    if (!text) return;
    setQuestion(text);
    setLoading(true);
    setPreview(null);
    setSavedMetric(null);
    setError(null);
    try {
      const { data } = await api.post("/ask/preview", { message: text, workspace_id: active.id });
      setPreview(data);
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.message === "Network Error" ? "Couldn't reach the server. Check your connection and try again." : e.message)
        || "Preview failed";
      setError({ message: detail, status: e.response?.status });
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const saveMetric = async () => {
    if (!preview?.sql) return;
    setSaving(true);
    try {
      const { data } = await api.post("/ask/save-metric", {
        workspace_id: active.id,
        sql: preview.sql,
        question: preview.question || question,
        definition: preview.understanding || "",
        source_tables: preview.tables || [],
        dimensions: preview.columns || [],
      });
      setSavedMetric(data.metric);
      toast.success(`Saved “${data.metric.name}” as an active governed metric`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not save metric");
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    run();
  };

  return (
    <div data-testid={ASK_PREVIEW.root} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-4xl px-6 py-10">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
            <ScanSearch className="h-6 w-6 text-accent" /> Explain a question
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            See exactly how Sema understands a question and the governed, read-only SQL it would run —
            transparent and <span className="text-accent">never executed</span> here.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-6">
          <Textarea
            data-testid={ASK_PREVIEW.input}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask anything about your data…  e.g. Which facility has the most readmissions?"
            rows={3}
            className="resize-none bg-card/60 text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSubmit(e);
            }}
          />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  data-testid={ASK_PREVIEW.example}
                  onClick={() => run(ex)}
                  className="rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground transition-colors hover:border-accent/50 hover:text-accent"
                >
                  {ex}
                </button>
              ))}
            </div>
            <Button
              type="submit"
              disabled={loading || !question.trim()}
              data-testid={ASK_PREVIEW.submit}
              className="rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Preview SQL
            </Button>
          </div>
        </form>

        {!preview && !loading && (
          <p data-testid={ASK_PREVIEW.empty} className="mt-16 text-center text-sm text-muted-foreground">
            Enter a question above to preview the question understanding, detected tables, join path and safe SQL.
          </p>
        )}

        {loading && (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-accent" />
          </div>
        )}

        {error && !loading && (
          <div data-testid={ASK_PREVIEW.error} className="mt-10 rounded-2xl border border-red-500/40 bg-red-500/5 px-6 py-10 text-center">
            <ShieldAlert className="mx-auto h-8 w-8 text-red-400" />
            <h2 className="mt-4 text-base font-semibold text-red-300">
              {error.status === 503 ? "AI provider not configured"
                : error.status === 502 ? "AI provider setup issue"
                : "Couldn't preview this question"}
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm text-muted-foreground">{error.message}</p>
            {(error.status === 503 || error.status === 502) && (
              <p className="mt-3 text-xs text-muted-foreground">
                Set <code className="text-accent">EMERGENT_LLM_KEY</code> or <code className="text-accent">OPENAI_API_KEY</code> in the backend environment, then retry.
              </p>
            )}
          </div>
        )}

        {preview?.error === "no_source" && (
          <div data-testid={ASK_PREVIEW.noSource} className="mt-10 rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-14 text-center">
            <Database className="mx-auto h-8 w-8 text-accent" />
            <h2 className="mt-4 text-lg font-semibold">No data source connected</h2>
            <p className="mt-2 text-sm text-muted-foreground">{preview.message}</p>
            <Link to="/schema" className="mt-5 inline-block text-xs uppercase tracking-[0.18em] text-accent hover:underline">
              Go to Schema →
            </Link>
          </div>
        )}

        {preview && !preview.error && (
          <div data-testid={ASK_PREVIEW.result} className="mt-8 space-y-4">
            {/* Understanding */}
            <Section icon={ScanSearch} title="Understanding" testid={ASK_PREVIEW.understanding}>
              <div className="flex flex-wrap items-center gap-3">
                <Badge data-testid={ASK_PREVIEW.intent} variant="outline" className="rounded-full border-accent/40 bg-accent/10 px-2.5 py-0.5 text-[10px] uppercase text-accent">
                  {INTENT_LABEL[preview.intent] || preview.intent}
                </Badge>
                <ConfidenceBar value={preview.confidence} />
                {preview.confidence_boost && (
                  <span className="text-[10px] text-accent">+0.1 boost · {preview.confidence_boost.reasons.join(", ")}</span>
                )}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground/90">{preview.understanding}</p>
            </Section>

            {/* Detected tables + columns */}
            {(preview.tables?.length > 0 || preview.columns?.length > 0) && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Section icon={Database} title="Tables detected" testid={ASK_PREVIEW.tables}>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.tables.length ? preview.tables.map((t) => (
                      <span key={t} data-testid={ASK_PREVIEW.table} className="rounded-md border border-border/60 bg-background px-2 py-1 text-[11px] font-medium">{t}</span>
                    )) : <span className="text-xs text-muted-foreground">None</span>}
                  </div>
                </Section>
                <Section icon={Columns3} title="Columns used" testid={ASK_PREVIEW.columns}>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.columns.length ? preview.columns.map((c) => (
                      <span key={c} className="rounded-md border border-border/50 px-2 py-1 text-[11px] text-muted-foreground">{c}</span>
                    )) : <span className="text-xs text-muted-foreground">None</span>}
                  </div>
                </Section>
              </div>
            )}

            {/* Join path */}
            {preview.join_path?.found && (
              <Section icon={Route} title="Join path" testid={ASK_PREVIEW.joinPath}>
                <div className="mb-2 flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${preview.join_path.trusted ? "bg-accent/15 text-accent" : "bg-amber-500/15 text-amber-400"}`}>
                    {preview.join_path.trusted ? "trusted path" : `weakest hop ${preview.join_path.min_confidence}`}
                  </span>
                </div>
                <ol className="space-y-1.5">
                  {preview.join_path.hops.map((h, i) => (
                    <li key={i} data-testid={ASK_PREVIEW.joinHop} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/50 bg-background/60 px-3 py-2 text-[11px]">
                      <span className="text-muted-foreground">{i + 1}.</span>
                      <code className="text-accent">{h.join_expr || `${h.from_fqn} → ${h.to_fqn}`}</code>
                      {!h.trusted && <span className="text-[10px] text-amber-400">below threshold</span>}
                    </li>
                  ))}
                </ol>
              </Section>
            )}

            {/* Clarification */}
            {preview.decision === "clarify" && preview.clarification && (
              <Section icon={HelpCircle} title="Needs clarification" testid={ASK_PREVIEW.clarification}>
                <p className="text-sm text-foreground/90">{preview.clarification.question}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(preview.clarification.options || []).map((o) => (
                    <span key={o} className="rounded-full border border-border/60 px-3 py-1 text-[11px] text-muted-foreground">{o}</span>
                  ))}
                </div>
              </Section>
            )}

            {/* Refusal */}
            {preview.decision === "refuse" && (
              <Section icon={ShieldAlert} title="Blocked — restricted data" testid={ASK_PREVIEW.refusal}>
                <p className="text-sm text-foreground/90">
                  This question touches restricted columns ({(preview.restricted_columns || []).join(", ")}).
                  Access to sensitive fields requires elevated permission.
                </p>
              </Section>
            )}

            {/* Safe SQL */}
            {preview.sql ? (
              <Section icon={ShieldCheck} title="Safe SQL preview" testid={ASK_PREVIEW.sql}>
                <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-emerald-400">
                  <ShieldCheck className="h-3.5 w-3.5" /> read-only · not executed
                </div>
                <pre className="overflow-x-auto rounded-lg border border-border/60 bg-background/80 p-3 text-[12px] leading-relaxed text-foreground/90">
                  <code>{preview.sql}</code>
                </pre>
                {preview.executable && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      data-testid={ASK_PREVIEW.runInChat}
                      onClick={() => navigate(`/chat?q=${encodeURIComponent(preview.question)}`)}
                      className="rounded-full bg-foreground text-xs uppercase tracking-[0.14em] text-background hover:bg-foreground/90"
                    >
                      Run this in chat <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Button>
                    {savedMetric ? (
                      <Link
                        to={`/semantics/metrics/${savedMetric.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/10 px-4 py-2 text-xs uppercase tracking-[0.14em] text-accent transition-colors hover:bg-accent/20"
                      >
                        <BadgeCheck className="h-4 w-4" /> Active metric · view
                      </Link>
                    ) : (
                      <Button
                        variant="outline"
                        disabled={saving}
                        data-testid={ASK_PREVIEW.saveMetric}
                        onClick={saveMetric}
                        className="rounded-full border-accent/40 text-xs uppercase tracking-[0.14em] text-accent hover:bg-accent/10"
                      >
                        {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BookmarkPlus className="mr-1.5 h-4 w-4" />}
                        Save as governed metric
                      </Button>
                    )}
                  </div>
                )}
                {savedMetric && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Now active — Sema will reuse it and boost answer confidence. Rename or refine it on the
                    {" "}<Link to="/semantics/metrics" className="text-accent hover:underline">Metrics</Link> page.
                  </p>
                )}
              </Section>
            ) : preview.decision !== "refuse" && preview.decision !== "clarify" && preview.intent !== "schema_question" ? (
              <Section icon={ShieldCheck} title="Safe SQL preview" testid={ASK_PREVIEW.sql}>
                <p className="text-xs text-muted-foreground">No SQL was generated for this question.</p>
              </Section>
            ) : null}

            {/* Assumptions */}
            {preview.assumptions?.length > 0 && (
              <Section icon={Sparkles} title="Assumptions" testid={ASK_PREVIEW.assumptions}>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {preview.assumptions.map((a, i) => (
                    <li key={i} className="flex gap-2"><span className="text-accent">•</span>{a}</li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
